import type { EditableCompanyProfile } from "@/lib/cat/company-profile.types";
import type {
  EditableIndividualProfile,
  EditableIndividualWithIdentity,
} from "@/lib/cat/individual-profile.types";
import {
  isCompanyProfileComplete,
  isCompanyContactEditComplete,
  isIndividualIdentityComplete,
  isPartyProfileComplete,
} from "@/lib/cat/party-profile-validation";
import { firstNonEmptyPhone } from "@/lib/cat/individual-profile.types";
import type { RetailCompany } from "@/lib/cat/retail-client.types";
import { parseMdmContactUse } from "@/lib/cat/mdm-map-contacts";
import type { SaveDraftClientDataInput } from "@/lib/schemas/cat/client-step-save-draft";

export function individualToEditableProfile(
  individual: Pick<
    import("@/lib/cat/retail-client.types").RetailIndividual,
    | "email"
    | "addressLine"
    | "addressFloorApartment"
    | "addressBuildingResidence"
    | "postBox"
    | "postalCode"
    | "city"
    | "fixedPhone"
    | "mobilePhone"
  >,
): EditableIndividualProfile {
  return {
    email: individual.email,
    addressLine: individual.addressLine,
    addressFloorApartment: individual.addressFloorApartment,
    addressBuildingResidence: individual.addressBuildingResidence,
    postBox: individual.postBox,
    postalCode: individual.postalCode,
    city: individual.city,
    fixedPhone: { ...individual.fixedPhone },
    mobilePhone: { ...individual.mobilePhone },
  };
}

export function individualToEditableWithIdentity(
  individual: Pick<
    import("@/lib/cat/retail-client.types").RetailIndividual,
    | "firstName"
    | "lastName"
    | "email"
    | "addressLine"
    | "addressFloorApartment"
    | "addressBuildingResidence"
    | "postBox"
    | "postalCode"
    | "city"
    | "fixedPhone"
    | "mobilePhone"
  >,
): EditableIndividualWithIdentity {
  return {
    ...individualToEditableProfile(individual),
    firstName: individual.firstName,
    lastName: individual.lastName,
  };
}

export function companyToEditableProfile(
  company: RetailCompany,
): EditableCompanyProfile {
  return {
    companyDenomination: company.inseeDenomination ?? "",
    apeCode: company.inseeApe ?? company.ape,
    email: "",
    addressLine: company.inseeAddressLine ?? "",
    addressFloorApartment: company.inseeAddressFloorApartment ?? "",
    addressBuildingResidence: company.inseeAddressBuildingResidence ?? "",
    postBox: company.inseePostBox ?? "",
    postalCode: company.inseePostalCode ?? "",
    city: company.inseeCity ?? "",
    fixedPhone: { value: "", use: "unknown" },
    mobilePhone: { value: "", use: "unknown" },
  };
}

export function isCompanyRegisteredInRefPers(
  company: RetailCompany | null | undefined,
): boolean {
  return Boolean(company?.registeredInMdm && !company.mdmReferentialMissing);
}

type CompanyDraftSlice = Pick<
  SaveDraftClientDataInput,
  | "siretInput"
  | "companyMdmSourceId"
  | "companyRegisteredInMdm"
  | "companyDenomination"
  | "companyApeCode"
  | "companyAddressLine"
  | "companyAddressFloorApartment"
  | "companyAddressBuildingResidence"
  | "companyPostBox"
  | "companyPostalCode"
  | "companyCity"
  | "companyEmail"
  | "companyFixedPhone"
  | "companyFixedPhoneUse"
  | "companyMobilePhone"
  | "companyMobilePhoneUse"
>;

/** RefPers profile only — no INSEE fallback, no user edits. */
function buildCompanyDraftSliceFromRefPers(
  company: RetailCompany,
  siretInput: string,
): CompanyDraftSlice {
  const fixed = company.mdmFixedPhone?.trim() ?? "";
  const mobile = company.mdmMobilePhone?.trim() ?? "";

  return {
    siretInput: siretInput.trim() || null,
    companyMdmSourceId: company.mdmSourceId ?? null,
    companyRegisteredInMdm: true,
    companyDenomination: company.mdmDenomination?.trim() || null,
    companyApeCode: company.mdmApe?.trim() || null,
    companyAddressLine: company.mdmAddressLine?.trim() || null,
    companyAddressFloorApartment:
      company.mdmAddressFloorApartment?.trim() || null,
    companyAddressBuildingResidence:
      company.mdmAddressBuildingResidence?.trim() || null,
    companyPostBox: company.mdmPostBox?.trim() || null,
    companyPostalCode: company.mdmPostalCode?.trim() || null,
    companyCity: company.mdmCity?.trim() || null,
    companyEmail: company.mdmEmail?.trim() || null,
    companyFixedPhone: fixed || null,
    companyFixedPhoneUse: fixed
      ? parseMdmContactUse(company.mdmFixedPhoneUse)
      : null,
    companyMobilePhone: mobile || null,
    companyMobilePhoneUse: mobile
      ? parseMdmContactUse(company.mdmMobilePhoneUse)
      : null,
  };
}

export function buildClientDraftSlice(input: {
  clientFirstName: string;
  clientLastName: string;
  clientId: string | null;
  fiche: EditableIndividualProfile | null;
  searchEmail: string;
}): Pick<
  SaveDraftClientDataInput,
  | "clientAddressLine"
  | "clientAddressFloorApartment"
  | "clientAddressBuildingResidence"
  | "clientPostBox"
  | "clientCity"
  | "clientEmail"
  | "clientFirstName"
  | "clientLastName"
  | "clientFixedPhone"
  | "clientFixedPhoneUse"
  | "clientMobilePhone"
  | "clientMobilePhoneUse"
  | "clientPostalCode"
  | "clientId"
> {
  const fiche = input.fiche;
  const fixed = fiche?.fixedPhone.value.trim() ?? "";
  const mobile = fiche?.mobilePhone.value.trim() ?? "";

  return {
    clientFirstName: input.clientFirstName.trim(),
    clientLastName: input.clientLastName.trim(),
    clientId: input.clientId,
    clientAddressLine: fiche?.addressLine.trim() ?? "",
    clientAddressFloorApartment: fiche?.addressFloorApartment.trim() ?? "",
    clientAddressBuildingResidence:
      fiche?.addressBuildingResidence.trim() ?? "",
    clientPostBox: fiche?.postBox.trim() ?? "",
    clientCity: fiche?.city.trim() ?? "",
    clientEmail: fiche?.email.trim() || input.searchEmail.trim() || "",
    clientFixedPhone: fixed || null,
    clientFixedPhoneUse: fixed ? (fiche?.fixedPhone.use ?? "unknown") : null,
    clientMobilePhone: mobile || null,
    clientMobilePhoneUse: mobile ? (fiche?.mobilePhone.use ?? "unknown") : null,
    clientPostalCode: fiche?.postalCode.trim() ?? "",
  };
}

export function buildCompanyDraftSlice(input: {
  company: RetailCompany | null;
  companyEdit: EditableCompanyProfile | null;
  siretInput: string;
}): CompanyDraftSlice {
  const emptySlice: CompanyDraftSlice = {
    siretInput: input.siretInput.trim() || null,
    companyMdmSourceId: null,
    companyRegisteredInMdm: null,
    companyDenomination: null,
    companyApeCode: null,
    companyAddressLine: null,
    companyAddressFloorApartment: null,
    companyAddressBuildingResidence: null,
    companyPostBox: null,
    companyPostalCode: null,
    companyCity: null,
    companyEmail: null,
    companyFixedPhone: null,
    companyFixedPhoneUse: null,
    companyMobilePhone: null,
    companyMobilePhoneUse: null,
  };

  if (!input.company) {
    return emptySlice;
  }

  if (isCompanyRegisteredInRefPers(input.company)) {
    const base = buildCompanyDraftSliceFromRefPers(
      input.company,
      input.siretInput,
    );
    if (!input.companyEdit) {
      return base;
    }

    const fixed = input.companyEdit.fixedPhone.value.trim();
    const mobile = input.companyEdit.mobilePhone.value.trim();

    return {
      ...base,
      companyEmail: input.companyEdit.email.trim() || null,
      companyFixedPhone: fixed || null,
      companyFixedPhoneUse: fixed ? input.companyEdit.fixedPhone.use : null,
      companyMobilePhone: mobile || null,
      companyMobilePhoneUse: mobile ? input.companyEdit.mobilePhone.use : null,
    };
  }

  const profile = input.companyEdit ?? companyToEditableProfile(input.company);

  const fixed = profile.fixedPhone.value.trim();
  const mobile = profile.mobilePhone.value.trim();

  return {
    siretInput: input.siretInput.trim() || null,
    companyMdmSourceId: null,
    companyRegisteredInMdm: false,
    companyDenomination: profile.companyDenomination.trim() || null,
    companyApeCode: profile.apeCode.trim() || null,
    companyAddressLine: profile.addressLine.trim() || null,
    companyAddressFloorApartment: profile.addressFloorApartment.trim() || null,
    companyAddressBuildingResidence:
      profile.addressBuildingResidence.trim() || null,
    companyPostBox: profile.postBox.trim() || null,
    companyPostalCode: profile.postalCode.trim() || null,
    companyCity: profile.city.trim() || null,
    companyEmail: profile.email.trim() || null,
    companyFixedPhone: fixed || null,
    companyFixedPhoneUse: fixed ? profile.fixedPhone.use : null,
    companyMobilePhone: mobile || null,
    companyMobilePhoneUse: mobile ? profile.mobilePhone.use : null,
  };
}

export function isIndividualFicheComplete(
  fiche: EditableIndividualProfile | null,
): boolean {
  if (!fiche) return false;
  return isPartyProfileComplete(fiche, { requireEmailOrPhone: true });
}

export function isIndividualEditComplete(
  fiche: EditableIndividualWithIdentity | null,
): boolean {
  if (!fiche) return false;
  return (
    isIndividualIdentityComplete(fiche) && isIndividualFicheComplete(fiche)
  );
}

export function isCompanyEditComplete(
  profile: EditableCompanyProfile | null,
): boolean {
  if (!profile) return false;
  return isCompanyProfileComplete(profile);
}

export function isCompanyRegisteredContactEditComplete(
  profile: EditableCompanyProfile | null,
): boolean {
  if (!profile) return false;
  return isCompanyContactEditComplete(profile);
}

export { firstNonEmptyPhone };
